/**
 * ScanInvoiceModal — OCR invoice import flow, in a modal (GU-6981).
 *
 * Mirrors `BulkImportModal` in shape and contract:
 *   - Opens from a toolbar button on `ActivityEntryStep`.
 *   - Does NOT call `createBulkImport` itself — returns the extracted rows +
 *     the upload-session file ids via `onAccept`, and the entry step appends
 *     them to its in-memory staging table. Persistence happens on the Entry
 *     step's Submit, then the existing Evidence step lets the user attach
 *     the uploaded invoices to the freshly-created activities.
 *
 * Why a single screen (no activity-type pre-pick)? Invoices declare their
 * own scope/activityType — forcing the user to pick up front would force
 * per-utility-type batches and defeat "drop in 10 mixed invoices".
 */

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { AlertCircle, FileText, Info, Loader2, Sparkles, X } from 'lucide-react';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { ImportFileDropzone, MINE_TYPES } from '@features/files/ImportFileDropZone';
import { useExtractInvoiceMutation } from '../../../api/emissions';
import { type UseUploadSessionResult } from '../../../hooks/useUploadSession';
import { getErrorMessage } from '@utils/error';
import type { CreateActivityRequest } from '../../../types/emissions';
import type { ExtractInvoiceResult } from '../../../types/invoiceExtraction';
import { isFugitiveGas } from '@g17eco/emissions-config';

const MAX_FILES = 20;
const MAX_FILE_BYTES = 20 * 1024 * 1024;

interface FileSlot {
  clientId: string;
  filename: string;
  status: 'uploading' | 'extracting' | 'extracted' | 'error';
  sessionFileId?: string;
  result?: ExtractInvoiceResult;
  errorMessage?: string;
}

export interface ScanInvoiceAccept {
  /** Validated activity rows ready to be staged in the entry table. */
  activities: CreateActivityRequest[];
  /**
   * Session file ids for the uploaded invoices. Not consumed by the entry
   * step today (the invoices are already in the upload session shared with
   * `EvidenceStep` post-submit), but kept on the contract so the caller can
   * trace which row came from which file if it ever needs to.
   */
  sessionFileIds: string[];
}

export interface ScanInvoiceModalProps {
  isOpen: boolean;
  initiativeId: string;
  /**
   * Shared upload session from `ActivityFormFlow`. The modal calls
   * `uploadFile` on it directly so the resulting `SessionFile` is visible
   * on the existing `MultiAddEvidenceStep` after submit — the invoices end
   * up attached as evidence to the freshly-created activities via the
   * same `promoteSelected` flow ByActivity already uses.
   */
  uploadSession: UseUploadSessionResult;
  onAccept: (result: ScanInvoiceAccept) => void;
  onClose: () => void;
}

const confidenceTone = (confidence: number | undefined) => {
  if (confidence === undefined) {
    return 'tw:bg-muted tw:text-muted-foreground';
  }
  if (confidence >= 0.8) {
    return 'tw:bg-emerald-100 tw:text-emerald-800';
  }
  if (confidence >= 0.5) {
    return 'tw:bg-amber-100 tw:text-amber-800';
  }
  return 'tw:bg-red-100 tw:text-red-800';
};

/**
 * Build a CreateActivityRequest from one mapped invoice line. Returns null
 * when the line wasn't mapped (water/waste, unrecognised unit, etc.) — the
 * caller skips those.
 */
const lineToCreateRequest = (line: ExtractInvoiceResult['lines'][number]): CreateActivityRequest | null => {
  if (!line.activity) {
    return null;
  }
  const a = line.activity;
  return {
    scope: a.scope,
    activityType: a.activityType,
    periodStart: a.periodStart,
    periodEnd: a.periodEnd,
    quantity: a.quantity,
    unit: a.unit,
    region: a.region,
    regionFallback: true,
    yearFallback: true,
    ...(a.fuelType ? { fuelType: a.fuelType } : {}),
    ...(a.supplier ? { dataSource: a.supplier } : {}),
    // Refrigerant/gas split — mirrors the bulk-import parser's logic.
    // Scan-invoice doesn't currently extract refrigerant types, but if the
    // mapper ever maps a refrigerant utility we want it on the right field.
    ...(a.fuelType && isFugitiveGas(a.fuelType) ? {} : {}),
  };
};

export const ScanInvoiceModal = ({
  isOpen,
  initiativeId,
  uploadSession,
  onAccept,
  onClose,
}: ScanInvoiceModalProps) => {
  const slotIdPrefix = useId();
  const [slots, setSlots] = useState<FileSlot[]>([]);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [extractInvoice] = useExtractInvoiceMutation();

  // Reset every time the modal closes — open + re-open should start fresh.
  useEffect(() => {
    if (!isOpen) {
      setSlots([]);
      setBatchError(null);
    }
  }, [isOpen]);

  const updateSlot = useCallback((clientId: string, patch: Partial<FileSlot>) => {
    setSlots((prev) =>
      prev.map((s) => (s.clientId === clientId ? { ...s, ...patch } : s)),
    );
  }, []);

  const removeSlot = useCallback((clientId: string) => {
    setSlots((prev) => prev.filter((s) => s.clientId !== clientId));
  }, []);

  const processFile = useCallback(
    async (clientId: string, file: File) => {
      try {
        // `ensureSession()` returns the live `UploadSession` synchronously
        // after the (idempotent) server create — reading the hook's
        // `session` prop here would be stale on the very first upload
        // because React hasn't flushed the setSessionId update yet.
        const session = await uploadSession.ensureSession();
        const sessionFile = await uploadSession.uploadFile(file);
        updateSlot(clientId, { status: 'extracting', sessionFileId: sessionFile.fileId });

        const result = await extractInvoice({
          initiativeId,
          sessionId: session._id,
          fileId: sessionFile.fileId,
        }).unwrap();

        updateSlot(clientId, { status: 'extracted', result });
      } catch (err) {
        const message = getErrorMessage(err);
        updateSlot(clientId, { status: 'error', errorMessage: message });
      }
    },
    [extractInvoice, initiativeId, updateSlot, uploadSession],
  );

  const handleFilesAdded = useCallback(
    (files: File[]) => {
      setBatchError(null);

      if (slots.length + files.length > MAX_FILES) {
        setBatchError(`You can upload up to ${MAX_FILES} invoices per batch.`);
        return;
      }

      const oversized = files.filter((f) => f.size > MAX_FILE_BYTES);
      if (oversized.length > 0) {
        setBatchError(
          `${oversized.map((f) => f.name).join(', ')} exceed${oversized.length === 1 ? 's' : ''} the 20 MB per-file limit.`,
        );
        return;
      }

      const newSlots: FileSlot[] = files.map((f, idx) => ({
        clientId: `${slotIdPrefix}-${Date.now()}-${idx}`,
        filename: f.name,
        status: 'uploading',
      }));

      setSlots((prev) => [...prev, ...newSlots]);

      newSlots.forEach((slot, idx) => {
        void processFile(slot.clientId, files[idx]);
      });
    },
    [processFile, slotIdPrefix, slots.length],
  );

  /**
   * Count of mappable consumption lines across all extracted slots — this
   * is what lands in the staging table on Accept. A single dual-fuel
   * invoice can contribute 2 to this count; a water-only invoice
   * contributes 0 (water lines map to `activity: null`).
   */
  const totalMappedLines = useMemo(
    () =>
      slots.reduce((sum, slot) => {
        if (slot.status !== 'extracted' || !slot.result) {
          return sum;
        }
        return sum + slot.result.lines.filter((l) => l.activity !== null).length;
      }, 0),
    [slots],
  );
  const stillProcessing = useMemo(
    () => slots.some((s) => s.status === 'uploading' || s.status === 'extracting'),
    [slots],
  );
  const canAccept = !stillProcessing && totalMappedLines > 0;

  const handleAccept = useCallback(() => {
    const activities: CreateActivityRequest[] = [];
    const sessionFileIds: string[] = [];
    for (const slot of slots) {
      if (slot.status !== 'extracted' || !slot.result) continue;
      let slotContributed = false;
      for (const line of slot.result.lines) {
        const request = lineToCreateRequest(line);
        if (request) {
          activities.push(request);
          slotContributed = true;
        }
      }
      // Track session files that contributed at least one row — those are
      // the invoices the user will see ready-to-attach on the post-submit
      // Evidence step.
      if (slotContributed && slot.sessionFileId) {
        sessionFileIds.push(slot.sessionFileId);
      }
    }
    if (activities.length === 0) {
      return;
    }
    onAccept({ activities, sessionFileIds });
  }, [slots, onAccept]);

  const acceptMap = useMemo(() => ({ ...MINE_TYPES.PDF, ...MINE_TYPES.IMAGE }), []);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size='lg' scrollable>
      <ModalHeader toggle={onClose}>Scan Invoices</ModalHeader>
      <ModalBody>
        <div className='tw:flex tw:gap-2 tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-3 tw:text-sm tw:mb-4 tw:text-amber-900'>
          <Sparkles className='tw:size-4 tw:shrink-0 tw:mt-0.5 tw:text-amber-700' />
          <div>
            <strong>This step uses AI.</strong> The invoices you upload will be sent to an external
            AI service (Anthropic Claude) to extract emission-relevant data. Don&apos;t upload
            documents containing personal data or information you don&apos;t want sent to a third
            party.
          </div>
        </div>

        <div className='tw:mb-4'>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-2'>
            Drop one or more PDFs or photos of utility bills (electricity, gas, fuel, water,
            waste, steam, heating, cooling). Up to {MAX_FILES} invoices per batch, 20 MB each.
          </p>
        </div>

        <ImportFileDropzone
          accept={acceptMap}
          multiple
          importFile={undefined}
          onFilesAdded={handleFilesAdded}
          dropText='Drag and drop your invoice files here'
          hint='Supported formats: PDF, PNG, JPEG, WebP. AI-powered extraction — files are sent to an external service.'
        />

        {batchError && (
          <div className='tw:flex tw:gap-2 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-red-800 tw:text-sm tw:mt-3'>
            <AlertCircle className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
            {batchError}
          </div>
        )}

        {slots.length > 0 && (
          <div className='tw:mt-4 tw:space-y-2'>
            {slots.map((slot) => (
              <FileSlotRow key={slot.clientId} slot={slot} onRemove={() => removeSlot(slot.clientId)} />
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ minHeight: '4rem' }}>
        <Button variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button
          className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0'
          onClick={handleAccept}
          disabled={!canAccept}
        >
          Add {totalMappedLines} {totalMappedLines === 1 ? 'activity' : 'activities'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const FileSlotRow = ({ slot, onRemove }: { slot: FileSlot; onRemove: () => void }) => {
  // Aggregate the per-line outcome into per-file UI state.
  //   `mappedLines`    — usable rows; will land in the staging table on Accept.
  //   `unmappedLines`  — lines Claude returned but the mapper couldn't classify
  //                      (water/waste, unrecognised unit). Surfaced so the user
  //                      knows the line was seen and the row count isn't a
  //                      surprise.
  //   `invoiceWarnings` — invoice-level signals (OCR failure, no consumption
  //                      data) that aren't tied to a specific line.
  const result = slot.status === 'extracted' ? slot.result : undefined;
  const mappedLines = result?.lines.filter((l) => l.activity !== null) ?? [];
  const unmappedLines = result?.lines.filter((l) => l.activity === null) ?? [];
  const invoiceWarnings = result?.warnings ?? [];
  const isFailedExtraction = slot.status === 'extracted' && mappedLines.length === 0;

  return (
    <div className='tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:shadow-sm tw:p-3'>
      <FileText className='tw:size-4 tw:text-muted-foreground tw:shrink-0 tw:mt-1' />
      <div className='tw:flex-1 tw:min-w-0'>
        <div className='tw:text-sm tw:font-medium tw:truncate'>{slot.filename}</div>

        {/* One summary line per mapped activity row. Multi-utility invoices
            (dual-fuel, etc.) produce multiple rows here. */}
        {mappedLines.map((line, idx) =>
          line.activity ? (
            <div key={`mapped-${idx}`} className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
              {line.activity.activityType} · {line.activity.quantity} {line.activity.unit} · {line.activity.region}
            </div>
          ) : null,
        )}

        {/* Lines Claude found but the mapper returned null for — water/waste,
            unrecognised unit, etc. Show the warning so the user knows why
            this line isn't being staged. */}
        {unmappedLines.map((line, idx) => (
          <div key={`unmapped-${idx}`} className='tw:text-xs tw:text-amber-700 tw:mt-1 tw:flex tw:items-start tw:gap-1'>
            <Info className='tw:size-3 tw:shrink-0 tw:mt-0.5' />
            <span>{line.warnings[0] ?? 'Line could not be classified.'}</span>
          </div>
        ))}

        {/* Invoice-level warnings (OCR failure, no consumption data) */}
        {invoiceWarnings.map((w, idx) => (
          <div key={`invoice-${idx}`} className='tw:text-xs tw:text-red-700 tw:mt-1 tw:flex tw:items-start tw:gap-1'>
            <Info className='tw:size-3 tw:shrink-0 tw:mt-0.5' />
            <span>{w}</span>
          </div>
        ))}

        {/* Transport error (network, RTK Query rejection) */}
        {slot.status === 'error' && (
          <div className='tw:text-xs tw:text-red-700 tw:mt-1'>{slot.errorMessage}</div>
        )}
      </div>
      <StatusBadge slot={slot} isFailedExtraction={isFailedExtraction} mappedCount={mappedLines.length} />
      <Button
        variant='secondary'
        onClick={onRemove}
        aria-label={`Remove ${slot.filename}`}
      >
        <X className='tw:size-4' />
      </Button>
    </div>
  );
};

/**
 * Status pill on the right edge of each file row. Tone follows the lowest
 * confidence across mapped lines (a single low-confidence line drags the
 * pill amber/red even if other lines are confident — surfacing the
 * weakest signal is more useful than averaging).
 */
const StatusBadge = ({
  slot,
  isFailedExtraction,
  mappedCount,
}: {
  slot: FileSlot;
  isFailedExtraction: boolean;
  mappedCount: number;
}) => {
  switch (slot.status) {
    case 'uploading':
      return (
        <Badge variant='outline' className='tw:gap-1'>
          <Loader2 className='tw:size-3 tw:animate-spin' /> Uploading
        </Badge>
      );
    case 'extracting':
      return (
        <Badge variant='outline' className='tw:gap-1'>
          <Loader2 className='tw:size-3 tw:animate-spin' /> Extracting
        </Badge>
      );
    case 'extracted': {
      if (isFailedExtraction) {
        return (
          <Badge variant='destructive' className='tw:gap-1'>
            <AlertCircle className='tw:size-3' /> Failed
          </Badge>
        );
      }
      // Lowest confidence across all mapped lines drives the pill tone.
      const confidences = (slot.result?.lines ?? [])
        .map((l) => l.activity?.confidence)
        .filter((c): c is number => typeof c === 'number');
      const minConfidence = confidences.length > 0 ? Math.min(...confidences) : undefined;
      return (
        <Badge className={cn('tw:gap-1', confidenceTone(minConfidence))}>
          {mappedCount > 1 ? `Extracted · ${mappedCount} lines` : 'Extracted'}
          {minConfidence !== undefined && ` · ${Math.round(minConfidence * 100)}%`}
        </Badge>
      );
    }
    case 'error':
      return (
        <Badge variant='destructive' className='tw:gap-1'>
          <AlertCircle className='tw:size-3' /> Failed
        </Badge>
      );
  }
};
